import { useState } from 'preact/hooks';
import type { EventDateRange, PublicEvent } from '../../shared/types';
import { adultAgeOf, ageOn, formatUsDate } from '../../shared/builtin';
import { localTodayIso } from '../../shared/dates';
import { isValidDate } from '../../shared/validate';
import { RegistrationForm } from '../pages/Register';

type Scenario = 'adult' | 'minor';
const AGES: Record<Scenario, number> = { adult: 30, minor: 12 };

/** The event's complete dates, or one sample date when none is complete yet: registration shows the event's dates. */
export function previewEventDates(dates: EventDateRange[], today = localTodayIso()): { dates: EventDateRange[]; sample: boolean } {
  const complete = dates.filter(d => isValidDate(d.start)).map(d => d.end && !isValidDate(d.end) ? { start: d.start } : d);
  return complete.length ? { dates: complete, sample: false } : { dates: [{ start: today }], sample: true };
}

/** A sample date of birth that makes the participant an adult or a minor on `today`, the day of signing, which decides
 * who signs. */
export function previewDateOfBirth(scenario: Scenario, today = localTodayIso()): string {
  const year = Number(today.slice(0, 4)) - AGES[scenario];
  const date = `${String(year).padStart(4, '0')}${today.slice(4, 10)}`;
  // February 29 has no matching birthday in most years; the day before keeps the age.
  return isValidDate(date) ? date : `${String(year).padStart(4, '0')}-02-28`;
}

/** The registration families see, run with synthetic answers. Nothing is saved, submitted or sent to Google.
 * `sampleDates` says the event's dates are placeholders from previewEventDates. */
export function ParticipantPreview({ event, placeholders, sampleDates }: { event: PublicEvent; placeholders?: boolean; sampleDates?: boolean }) {
  const [open, setOpen] = useState(false), [scenario, setScenario] = useState<Scenario>('minor'), [run, setRun] = useState(0);
  const today = localTodayIso(), dateOfBirth = previewDateOfBirth(scenario, today), age = ageOn(dateOfBirth, today);
  return <div class="stack">
    <div><button class="secondary" aria-expanded={open} onClick={() => setOpen(!open)}>{open ? 'Close participant preview' : 'Preview participant experience'}</button></div>
    {open && <div class="participant-preview">
      <p class="preview-banner">Participant preview — try the form here. Answers are not saved or submitted.{placeholders ? ' Event details are incomplete, so placeholder details fill in.' : ''}</p>
      <div class="row wrap preview-scenario">
        <label>Preview as<select aria-label="Preview participant age" value={scenario} onChange={e => setScenario(e.currentTarget.value as Scenario)}><option value="minor">A participant under {adultAgeOf(event)}</option><option value="adult">An adult participant</option></select></label>
        <button class="secondary" onClick={() => setRun(r => r + 1)}>Reset preview answers</button>
      </div>
      <p class="field-help preview-sample-dates" role="status">Sample date of birth: {formatUsDate(dateOfBirth)}, so the participant is {age} today, the day of signing.{sampleDates ? ' The event has sample dates until you add the real ones under Event details.' : ''}</p>
      <p class="field-help">The age decides which signatures each person needs. Switching starts the preview over.</p>
      <RegistrationForm key={`${scenario}:${run}`} ev={event} preview previewBuiltin={{ dateOfBirth }} />
    </div>}
  </div>;
}
